import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  useParams,
  useNavigate,
  useLocation,
  Link as ReactRouterLink,
} from 'react-router-dom';

import { DndContext, closestCorners } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import {
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  LinkBox,
  Stack,
  Skeleton,
  FormControl,
  FormLabel,
  Switch,
} from '@chakra-ui/react';

import { EditIcon, DeleteIcon } from '@chakra-ui/icons';
import { MdSettings } from 'react-icons/md';

import { ReducerStatus, CompanionMode } from 'utils/constants';

import { fetchCourseByIdAction } from 'store/modules/course/course-actions';
import {
  fetchCourseQuestsAction,
  updateCourseQuestOrderNoAction,
} from 'store/modules/quest/quest-actions';

import { courseActions } from 'store/modules/course/course-slice';
import { questActions } from 'store/modules/quest/quest-slice';
import { companionActions } from 'companion/store/companion-slice';
import {
  handleErrorAndNavigate,
  createErrorInfoFromAction,
} from 'utils/errorHandler';

import CreateQuestMd from 'components/quest/modals/CreateQuestMd';
import Breadcrumbs from 'components/common/Breadcrumbs';
import CourseHeader from 'components/course/CourseHeader';
import UpdateCourseMd from 'components/course/modals/UpdateCourseMd';
import DeleteCourseMd from 'components/course/modals/DeleteCourseMd';
import CourseQuestsDraft from 'components/course/CourseQuestsDraft';
import QuestAdminItem from 'components/quest/QuestAdminItem';
import UpdateQuestMd from 'components/quest/modals/UpdateQuestMd';
import DeleteQuestMd from 'components/quest/modals/DeleteQuestMd';
import RemoveQuestMd from 'components/quest/modals/RemoveQuestMd';
import UpdateInteractiveContentMd from 'components/subQuest/modals/interactiveContents/UpdateInteractiveContentMd';
import DeleteSubQuestMd from 'components/subQuest/modals/DeleteSubQuestMd';
import UpdateVideoMd from 'components/subQuest/modals/videos/UpdateVideoMd';
import AddQuestSubQuestsMd from 'components/quest/modals/AddQuestSubQuestsMd';

import CreateQuizMd from 'components/subQuest/modals/quizzes/CreateQuizMd';
import { BiPieChart } from 'react-icons/bi';
import UpdateNarrativeMd from 'components/subQuest/modals/narrative/UpdateNarrativeMd';
import UpdateFileSubmissionMd from 'components/subQuest/modals/fileSubmissions/UpdateFileSubmissionMd';
import { publishEntityActions } from 'store/modules/common/publishEntity/publishEntity-slice';
import RemoveQuestSubQuestMd from 'components/quest/modals/RemoveQuestSubQuestMd';

function CourseAdminPage() {
  const { id } = useParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const course = useSelector((state) => state.course.course);
  const courseQuests = useSelector((state) => state.quest.courseQuests);
  const getCourseStatus = useSelector((state) => state.course.getCourseStatus);
  const getQuestsStatus = useSelector((state) => state.quest.getQuestsStatus);
  const publishStatus = useSelector(
    (state) => state.publishEntity.updatePublishEntity.status
  );

  // Configuration for ErrorDisplay component - memoized to prevent useEffect dependency changes
  const adminCourseEntityConfig = useMemo(
    () => ({
      name: 'Course',
      homeRoute: '/admin/courses',
    }),
    []
  );

  // Fetch course with error handling
  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const result = await dispatch(fetchCourseByIdAction(id));

        // Check if the action was rejected
        if (fetchCourseByIdAction.rejected.match(result)) {
          // Create standardized error info from Redux action failure
          const errorInfo = createErrorInfoFromAction(
            result,
            'Error Loading Course',
            'An unexpected error occurred while loading the course.'
          );

          // Handle error and navigate using utility function
          handleErrorAndNavigate(
            dispatch,
            navigate,
            location,
            errorInfo,
            adminCourseEntityConfig
          );
          return;
        }

        // If successful, set up companion AI outlines
        dispatch(companionActions.setOutlines([]));
      } catch (error) {
        // Handle unexpected errors
        const errorInfo = createErrorInfoFromAction(
          { payload: error },
          'Error Loading Course',
          'An unexpected error occurred while loading the course.'
        );

        handleErrorAndNavigate(
          dispatch,
          navigate,
          location,
          errorInfo,
          adminCourseEntityConfig
        );
      }
    };

    fetchCourse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, dispatch, navigate, adminCourseEntityConfig]); // location is intentionally excluded

  // Fetch course's quests
  useEffect(() => {
    if (course && getCourseStatus === ReducerStatus.SUCCEEDED) {
      document.title = `Skill Quest | Admin - ${course.name}`;
      dispatch(fetchCourseQuestsAction(course.id));
    }
  }, [course, getCourseStatus, dispatch]);

  // Setup AI context
  useEffect(() => {
    if (course) {
      let couresContext = `CourseName: ${course.name} | Course Description: ${course.description}`;

      if (courseQuests.length > 0) couresContext += '| Current Topics: ';

      for (let i = 0; i < courseQuests.length; i += 1) {
        const { name, description } = courseQuests[i].quest;
        couresContext += `{Topic: ${name} Description: ${description}}`;
      }

      dispatch(companionActions.setContext(couresContext));
      dispatch(companionActions.setMode(CompanionMode.COURSE_OUTLINE));

      dispatch(
        companionActions.setGreetings(
          "I'm your AI Companion Bot ready to assist you. \n \n" +
            `Currently, you're editing "${course.name}". I'm here to help you with anything you need, whether it's understanding course content, creating engaging materials, or mastering new concepts. \n \n` +
            "Just let me know how I can assist you further, and together we'll make your learning experience productive and enjoyable!📚"
        )
      );
    }
  }, [course, courseQuests, dispatch]);

  // Reset publish status after SUCCESS/FAIL
  useEffect(() => {
    if (
      publishStatus === ReducerStatus.SUCCEEDED ||
      publishStatus === ReducerStatus.FAILED
    ) {
      dispatch(publishEntityActions.resetPublishStatus());
    }
  }, [publishStatus, dispatch]);

  const handlerOpenDeleteConfirmation = () => {
    dispatch(courseActions.toggleDeleteModal(true));
  };

  const handleOpenUpdateCourseModal = () => {
    dispatch(courseActions.toggleUpdateModal(true));
  };

  const getCourseQuestById = (selectedId) =>
    courseQuests.findIndex((courseQuest) => courseQuest.id === selectedId);

  const handleChangeOrderNo = (event) => {
    const { active, over } = event;

    if (active.id === over.id) return;

    const originalPos = getCourseQuestById(active.id);
    const newPos = getCourseQuestById(over.id);

    dispatch(
      updateCourseQuestOrderNoAction({
        courseId: id,
        questId: courseQuests.find(
          (courseQuest) => courseQuest.id === active.id
        ).quest.id,
        newOrderNo: newPos + 1,
      })
    );

    dispatch(
      questActions.setCourseQuests(arrayMove(courseQuests, originalPos, newPos))
    );
  };

  return (
    <>
      {/* <SideNavigationSm isOpen={isOpen} onClose={onClose} /> */}
      <UpdateCourseMd />
      <UpdateQuestMd />
      <UpdateVideoMd />
      <UpdateNarrativeMd />
      <UpdateFileSubmissionMd />
      <DeleteCourseMd />
      <DeleteQuestMd />
      <RemoveQuestMd />
      <DeleteSubQuestMd />
      <UpdateInteractiveContentMd />
      <CreateQuizMd />
      <AddQuestSubQuestsMd />
      <RemoveQuestSubQuestMd />

      <Flex minH="100vh" ml={{ base: 0, md: 20 }}>
        {/* <SideNavigation /> */}
        <Box flex="1" ml={2} p="5">
          <Flex direction={{ base: 'column', lg: 'row' }}>
            <Box flex="1">
              <Breadcrumbs
                mb={4}
                links={[
                  { name: 'Home', path: '/' },
                  { name: 'Courses', path: '/admin/courses' },
                  {
                    name: course?.name,
                    path: `/admin/courses/${id}`,
                  },
                ]}
              />

              <Flex direction={{ base: 'column', sm: 'row' }}>
                <CourseHeader
                  isLoading={getCourseStatus === ReducerStatus.LOADING}
                  course={course}
                  isAdminMode
                  flex="1"
                  mb={2}
                />
                <Flex gap={4} height="fit-content">
                  {course?.isEditable && (
                    <FormControl display="flex" alignItems="center" w="auto">
                      <FormLabel
                        htmlFor="edit-mode-switch"
                        mb="0"
                        fontSize="sm"
                      >
                        Edit Mode
                      </FormLabel>
                      <Switch
                        id="edit-mode-switch"
                        colorScheme="teal"
                        isChecked
                        onChange={() => navigate(`/courses/${id}`)}
                      />
                    </FormControl>
                  )}
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      size={{ base: 'sm', md: 'sm' }}
                      colorScheme="teal"
                      aria-label="Options"
                      icon={<MdSettings />}
                      variant="outline"
                    />
                    <MenuList>
                      <MenuItem
                        icon={<EditIcon />}
                        onClick={() => handleOpenUpdateCourseModal()}
                      >
                        Edit Course
                      </MenuItem>
                      <MenuItem
                        icon={<DeleteIcon />}
                        onClick={() => handlerOpenDeleteConfirmation()}
                      >
                        Delete Course
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </Flex>
              </Flex>

              <Flex
                mt={4}
                direction={{ base: 'column', md: 'row' }}
                justify="end"
                gap={4}
              >
                {course && <CreateQuestMd id={parseInt(id, 10)} />}
                <LinkBox
                  as={ReactRouterLink}
                  to={`/admin/reports/sub-quests?course=${parseInt(id, 10)}`}
                  size="sm"
                >
                  <Button rightIcon={<BiPieChart />} colorScheme="blue">
                    Report
                  </Button>
                </LinkBox>
              </Flex>

              {course && <CourseQuestsDraft />}

              <Box mt={7}>
                {getQuestsStatus === ReducerStatus.LOADING ? (
                  <Stack>
                    <Skeleton mb={6} height="72px" borderRadius="md" />
                    <Skeleton mb={6} height="72px" borderRadius="md" />
                    <Skeleton mb={6} height="72px" borderRadius="md" />
                    <Skeleton mb={6} height="72px" borderRadius="md" />
                  </Stack>
                ) : (
                  <DndContext
                    onDragEnd={handleChangeOrderNo}
                    collisionDetection={closestCorners}
                  >
                    <SortableContext
                      items={courseQuests}
                      strategy={verticalListSortingStrategy}
                    >
                      {courseQuests.map((courseQuest) => (
                        <QuestAdminItem
                          key={courseQuest.id}
                          courseId={course.id}
                          id={courseQuest.id}
                          courseQuest={courseQuest}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                )}
              </Box>
            </Box>

            {/* <Activities /> */}
          </Flex>
        </Box>
      </Flex>
    </>
  );
}

export default CourseAdminPage;
